<template>
	<div class="container-fluid p-3">
		<div class="my-2 text-center">
					<h2 class="my-auto">Order Responses</h2>
			</div>
			<div class="">
				<div class="d-flex justify-content-between align-items-end mb-2 flex-wrap" v-if="orderResponseLineOrderLogs?.length > 0">
					<!-- Mass actions left -->
					<div class="d-flex align-items-end mb-2">
						<div class="mr-2">
							<b-form-select
								v-model="selectedMassAction"
								:options="massActionOptions"
								size="sm"
								style="min-width: 180px;"
							/>
						</div>

						<b-button
							size="sm"
							variant="primary"
							:disabled="!selectedMassAction || selectedOrderResponses.length === 0"
							@click="runMassAction(false)"
						>
							Ausführen
						</b-button>

						<span class="ml-2 text-muted" v-if="selectedOrderResponses.length > 0">
      				{{ selectedOrderResponses.length }} ausgewählt
    				</span>
					</div>

					<!-- Filters right -->
					<div class="d-flex align-items-end mb-2" >
						<div class="mr-2">
							<multi-select-wrapper
									:value="filters.vendorIds"
									@input="filters.vendorIds = $event"
									:options="vendorOptions"
									:placeholder="'Händler'"
									customClass="mb-0 order-response-vendor-select"
									style="max-width: 200px;"
									:small="true"
							/>
						</div>

						<div class="mr-2">
							<b-form-input
								v-model="filters.search"
								size="sm"
								placeholder="BestellNr, Ean, Meldestatus..."
								style="min-width: 220px;"
							/>
						</div>

						<div class="d-flex align-items-end">


							<b-button
									size="sm"
									variant="outline-secondary"
									@click="resetFilters()"
							>
								Zurücksetzen
							</b-button>
						</div>
					</div>
				</div>

				<b-tabs content-class="mt-3">
					<b-tab :title="'rejected / cancelled (' + rejectOrderResponseCount + ')'" active @click="activeStatusTab = 'rejected'">
					</b-tab>

					<b-tab :title="'partially shipped (' + partialShippedOrderResponseCount + ')'" @click="activeStatusTab = 'partialShipped'">
					</b-tab>

					<b-tab :title="'backorder (' + backorderOrderResponseCount + ')'" @click="activeStatusTab = 'backorder'">
					</b-tab>

					<b-tab :title="'unknown (' + unknownOrderResponseCount + ')'" @click="activeStatusTab = 'unknown'">
					</b-tab>
				</b-tabs>
				<b-table :fields="fields"
					:items="currentOrderResponseLines"
					striped
					hover
					v-if="currentOrderResponseLines?.length > 0">

					<template #head(selected)>
						<b-form-checkbox

								@change="toggleSelectAll"
						/>
					</template>

					<template #cell(selected)="row">
						<b-form-checkbox
								v-model="row.item.selected"
						/>
					</template>

					<template #cell(customerOrderId)="row">
						<a
								v-if="row?.item?.order_log"
								:href="`/customerorder/${row.item?.order_log.customerorder.id}`"
								target="_blank"
								rel="noopener noreferrer"
						>
							{{ row.item.order_log.customerorder.id }}
						</a>
						<span v-else>-</span>
					</template>

					<template #cell(ean)="row">
						<div v-if="row?.item?.order_log" class="d-flex align-items-center">
							<a

									:href="`/material/${row.item.order_log.customerorder.customerorderitems[0].stock.material.id}/edit`"
									target="_blank"
									rel="noopener noreferrer"
							>
								{{ row.item.orderresponseline.ean }}
							</a>
							<copy-to-clipboard-component
									:text-to-copy="row?.item?.orderresponseline?.ean"
									:alert-display-length="1"
									:alert-show-copied-text="true"
									class="ml-2"
							>
								<b-button
										type="button"
										size="sm"
										variant="outline-secondary"
								>
									<i class="bi bi-clipboard"></i>
								</b-button>
							</copy-to-clipboard-component>
						</div>
						<span v-else>
							<copy-to-clipboard-component alertDisplayLength='1' alertShowCopiedText="true">
								<span>{{ row?.item?.orderresponseline?.ean}}</span>
							</copy-to-clipboard-component>
						</span>
					</template>

					<template #cell(orderQuantity)="row">
						<span>{{ row?.item?.order_log?.customerorder?.customerorderitems[0].quantity}}</span>
					</template>

					<template #cell(cancelledQuantity)="row">
						<span>
							{{ row?.item.orderresponseline?.quantityOrdered == null || row?.item.orderresponseline?.quantityConfirmed == null
												? '-'
												: row.item.orderresponseline.quantityOrdered - row.item.orderresponseline.quantityConfirmed
							}}
						</span>
					</template>

					<template #cell(articlePriceNet)="row">
						<span>{{ formatEuroCurrency(getCustomerOrder(row), getArticlePriceNet(row)) }}</span>
						<span v-if="getOriginalCurrencyText(row)">
							<br>{{ getOriginalCurrencyText(row) }}
						</span>
					</template>

					<template #cell(lastCancellationDate)="row">
						<span>{{ row?.item?.order_log?.customerorder?.lastCancelationDate}}</span>
					</template>

					<template #cell(reportDate)="row">
						<span>{{ row?.item?.order_log?.customerorder?.reportDate}}</span>
					</template>

					<template #cell(reportStatus)="row">
						<span>{{ getStatusLabel(row?.item.orderresponseline.status) + ( row?.item?.orderresponseline.additionalStatusInfo ? ' - ' + row?.item?.orderresponseline.additionalStatusInfo : '')}}</span>
					</template>

					<template #cell(originalVendor)="row">
						<span>{{ getVendorName(row?.item?.orderresponseline.orderresponse.vendorId) }} / {{ getMarketAccount(row)}}</span>
					</template>

					<template #cell(shipmentInbound)="row">
						<span>{{ row?.item?.order_log?.customerorder?.maybeInboundShipmentDate}}</span>
					</template>

					<template #cell(importDate)="row">
						<span>{{ row?.item?.createdAt ? new Date(row.item.createdAt).toLocaleString('de-DE') : '-' }}</span>
					</template>

					<template #cell(destinationCountry)="row">
						<span>{{ row?.item?.order_log?.customerorder?.address?.country?.countryName}}</span>
					</template>

					<template #cell(action)="row">
						<span>
							<b-dropdown size="sm" variant="primary" text="Aktion" right block v-if="row?.item?.order_log?.customerorder">
								<b-dropdown-item-button>
									<!--Freigeben WNk-->
									<b-button
											@click="() => { openUnfreezeModal(row?.item) }"
											size="sm"
											:disabled="disableButton(row?.item?.order_log?.customerorder)"
											:variant="(httpRequestInProgress) ? 'success': 'primary'"
											block
									><!-- button -->
											<span v-if="httpRequestInProgress">
													<b-spinner
															type="grow"
															label="Loading..."
															small
													/>
													</span>
											<span v-else>Freigeben WNK</span>

									</b-button>
								</b-dropdown-item-button>

								<!--Storno-->
								<b-dropdown-item-button>
									<coi-action-bar-refund
										:customerOrder="row?.item?.order_log?.customerorder"
										:orderStates="orderStates"
										:authorization="authorization"
										:is-order-response-action="true"
										@success="markAsProcessed(row.item)"
									/>
								</b-dropdown-item-button>

								<!--Umbestellen-->
								<b-dropdown-item-button>
									<coi-action-bar-change-vendor
										:customerOrder="row?.item?.order_log?.customerorder"
										:orderStates="orderStates"
										:vendors="vendors"
										:authorization="authorization"
										@success="markAsProcessed(row.item)"
									></coi-action-bar-change-vendor>
								</b-dropdown-item-button>

								<b-dropdown-item-button>
									<!--Löschen-->
									<b-button
											@click="openDeleteModal(row?.item)"
											size="sm"
											:disabled="httpRequestInProgress"
											variant="danger"
											block
									><!-- button -->
											<span v-if="httpRequestInProgress">
													<b-spinner
															type="grow"
															label="Loading..."
															small
													/>
													</span>
											<span v-else>Löschen</span>
									</b-button>
								</b-dropdown-item-button>
							</b-dropdown>

							<!--Löschen-->
									<b-button
											@click="openDeleteModal(row?.item, false)"
											size="sm"
											:disabled="httpRequestInProgress"
											variant="danger"
											block
											v-else
									><!-- button -->
											<span v-if="httpRequestInProgress">
													<b-spinner
															type="grow"
															label="Loading..."
															small
													/>
													</span>
											<span v-else>Löschen</span>
									</b-button>
						</span>
					</template>

				</b-table>
				<div v-else class="text-center text-muted">
					Keine Order Responses gefunden.
				</div>
				<div class="mt-4">
					<div class="my-2 text-center">
						<h3 class="my-auto">Order Responses ohne Orderlog</h3>
					</div>
					<!-- Mass actions left -->
					<div class="d-flex align-items-end mb-2" 	v-if="unmatchedOrderResponseLines?.length > 0">
						<div class="mr-2">
							<b-form-select
									v-model="selectedUnmatchedMassAction"
									:options="massActionOptions"
									size="sm"
									style="min-width: 180px;"
							/>
						</div>

						<b-button
								size="sm"
								variant="primary"
								:disabled="!selectedUnmatchedMassAction || selectedUnmatchedOrderResponses.length === 0"
								@click="runMassAction(true)"
						>
							Ausführen
						</b-button>

						<span class="ml-2 text-muted" v-if="selectedUnmatchedOrderResponses.length > 0">
      				{{ selectedUnmatchedOrderResponses.length }} ausgewählt
    				</span>
					</div>

					<b-table
							:fields="unmatchedFields"
							:items="currentUnmatchedOrderResponseLines"
							striped
							hover
							v-if="unmatchedOrderResponseLines?.length > 0"
					>

						<template #head(selected)>
							<b-form-checkbox

									@change="toggleSelectAllUnmatched"
							/>
						</template>

						<template #cell(selected)="row">
							<b-form-checkbox
									v-model="row.item.selected"
							/>
						</template>

						<template #cell(ean)="row">
							<div class="d-flex align-items-center">
								<a
										:href="`/material/${row.item.materialId}/edit`"
										target="_blank"
										rel="noopener noreferrer"
								>
									{{ row.item.ean }}
								</a>
								<copy-to-clipboard-component
										:text-to-copy="row?.item?.ean"
										:alert-display-length="1"
										:alert-show-copied-text="true"
										class="ml-2"
								>
									<b-button
											type="button"
											size="sm"
											variant="outline-secondary"
									>
										<i class="bi bi-clipboard"></i>
									</b-button>
								</copy-to-clipboard-component>
							</div>
						</template>

						<template #cell(cancelledQuantity)="row">
					<span>
						{{ row?.item?.quantityOrdered == null || row?.item?.quantityConfirmed == null
							? '-'
							: row.item.quantityOrdered - row.item.quantityConfirmed
						}}
					</span>
						</template>

						<template #cell(reportStatus)="row">
							<span>{{ getStatusLabel(row?.item.status) + ( row?.item?.additionalStatusInfo ? ' - ' + row?.item?.additionalStatusInfo : '')}}</span>
						</template>

						<template #cell(originalVendor)="row">
							<span>{{ getVendorName(row?.item?.orderresponse.vendorId) }}</span>
						</template>

						<template #cell(importDate)="row">
							<span>{{ row?.item?.createdAt ? new Date(row.item.createdAt).toLocaleString('de-DE') : '-' }}</span>
						</template>

						<template #cell(action)="row">
							<span>
								<b-button
										@click="openDeleteModal(row?.item, true)"
										size="sm"
										:disabled="httpRequestInProgress"
										variant="danger"
										block
								><!-- button -->
										<span v-if="httpRequestInProgress">
												<b-spinner
														type="grow"
														label="Loading..."
														small
												/>
												</span>
										<span v-else>Löschen</span>
								</b-button>
							</span>
						</template>
					</b-table>
					<div v-else class="text-center text-muted">
						Keine Order Responses ohne Orderlog gefunden.
					</div>
				</div>
		</div>
		<!--Delete modal		-->
		<b-modal
				v-model="deleteModalState"
				@ok="markAsProcessed(orderResponseToDelete, false, !Object.hasOwn(orderResponseToDelete, 'order_log'))"
				cancel-title="abbrechen"
				hide-header
				size="sm"
		>
			{{"Diese Orderresponse wirklich löschen?" }}

		</b-modal>

		<!-- Freigabe WNK Modal		-->
		<b-modal
				v-model="unfreezeModalState"
				id="modal-unfreeze-order"
				title="Bestellung Freigeben"
				@ok="unfreezeOrderWNC(orderResponseToUnfreeze)"
				cancel-title="abbrechen"
				size="md"
		>
			Diese Bestellung wirklich freigeben?

			<div class="my-3">
				<label class="form-label">Soll beim alten Lieferanten storniert werden?</label>
				<b-form-checkbox
						v-model="shouldCreateVendorCancellation"
						:value="true"
						:unchecked-value="false">
				</b-form-checkbox>
			</div>
		</b-modal>

		<!--Error modal		-->
		<modal-error-component v-model="modalError"></modal-error-component>
	</div>
</template>

<script>
import ModalErrorComponent from "./ModalErrorComponent.vue";
import CopyToClipboardComponent from "./util/CopyToClipboardComponent.vue";
import MultiSelectWrapper from "./util/MultiSelectWrapper.vue";
import {useCustomerOrderActions} from "../composables/useCustomerOrderActions";
import { useCurrencyFormatting } from '../composables/useCurrencyFormatting';
const currencyFormatting = useCurrencyFormatting();

export default {
	name: "OrderResponseTable",
	components: {MultiSelectWrapper, CopyToClipboardComponent, ModalErrorComponent},
	props: {
		passedOrderResponseLineOrderLogs: {
			type: Array,
			required: true,
		},
		passedUnmatchedOrderResponseLines: {
			type: Array,
			required: true,
		},
		vendors: {
			type: Array,
			required: true,
		},
	},
	data() {
		return {
			orderResponseLineOrderLogs: [],
			unmatchedOrderResponseLines: [],
			fields: [
				{ key: "selected", label: "" },
				{ key: "customerOrderId", label: "BestellNr" },
				{ key: "ean", label: "Ean", sortable: true },
				{ key: "orderQuantity", label: "Bestell Menge" },
				{ key: "cancelledQuantity", label: "Stornierte Menge" },
				{ key: "lastCancellationDate", label: "Gepl. Versanddatum" },
				{ key: "reportDate", label: "Meldedatum" },
				{ key: "reportStatus", label: "Meldestatus" },
				{ key: "originalVendor", label: "O-händler / Markt", sortable: true },
				{ key: "shipmentInbound", label: "Im Zulauf" },
				{ key: "articlePriceNet", label: "Artikelpreis" },
				{ key: "destinationCountry", label: "Zielland" },
				{ key: "importDate", label: "Import Datum" },
				{ key: "action", label: "" },
			],
			unmatchedFields: [
				{ key: "selected", label: "" },
				{ key: "ean", label: "Ean", sortable: true },
				{ key: "cancelledQuantity", label: "Stornierte Menge" },
				{ key: "reportStatus", label: "Meldestatus" },
				{ key: "originalVendor", label: "O-händler", sortable: true },
				{ key: "importDate", label: "Import Datum" },
				{ key: "action", label: "" },
			],
			authorization: null,
			orderStates: {
				types: {
					"-1": "Offen",
					0: "Neu",
					1: "Erledigt",
					2: "Freigegeben",
					3: "Erstattung ausstehend",
					4: "Erstattet",
				},
				codes: {
					STATUS: -2,
					OPEN: -1,
					NEW: 0,
					DONE: 1,
					WAIT: 2,
					REFUND: 3,
					REFUND_OK: 4,
				},
			},
			modalError: {
				show: false,
				message: 'unpopulated',
				error: 'unpopulated',
				title: 'unpopulated'
			},
			httpRequestInProgress: false,
			unfreezeModalState: false,
			deleteModalState: false,
			isMassAction: false,
			selectedMassAction: null,
			selectedUnmatchedMassAction: null,
			OrderResponseToCancel: null,
			selectedVendorCancellationQuantity: 1,
			orderResponseToDelete: null,
			orderResponseToUnfreeze: null,
			shouldCreateVendorCancellation: false,
			activeStatusTab: 'rejected',

			massActionOptions: [
				{ value: null, text: 'Aktion auswählen' },
				{ value: 'delete', text: 'Löschen' },
			],

			filters: {
				vendorIds: [],
				search: '',
			},
		};
	},
	watch: {
		passedOrderResponseLineOrderLogs: {
			handler(newVal) {
				this.orderResponseLineOrderLogs = newVal
				this.orderResponseLineOrderLogs = this.orderResponseLineOrderLogs.map(log => ({
					...log,
					selected: false,
				}));

			},
			immediate: true,
		},
		passedUnmatchedOrderResponseLines: {
			handler(newVal) {
				this.unmatchedOrderResponseLines = newVal
				this.unmatchedOrderResponseLines = this.unmatchedOrderResponseLines.map(log => ({
					...log,
					selected: false,
				}));

			},
			immediate: true,
		},
	},
	computed: {
		filteredOrderResponseLineOrderLogs() {
			return this.orderResponseLineOrderLogs.filter(log => {
				const selectedVendorIds = (this.filters.vendorIds || []).map(vendor => Number(vendor.value));
				const lineVendorId = Number(log?.orderresponseline?.orderresponse?.vendorId);

				if (selectedVendorIds.length > 0 && !selectedVendorIds.includes(lineVendorId)) {
					return false;
				}

				if (this.filters.status && log.status !== this.filters.status) {
					return false;
				}

				if (this.filters.search) {
					const statusLabel = this.getStatusLabel(log.orderresponseline.status).toLowerCase();

					const search = this.filters.search.toLowerCase();

					return String(log.id).includes(search)
							|| String(log.ean ?? '').toLowerCase().includes(search)
							|| statusLabel.includes(search)
							|| String(log.orderresponseline?.additionalStatusInfo).toLowerCase()?.includes(search)
							|| String(log.order_log?.customerorder?.id).includes(search)
				}

				return true;
			});
		},

		filteredUnmatchedOrderResponseLines() {
			return this.unmatchedOrderResponseLines.filter(log => {
				const selectedVendorIds = (this.filters.vendorIds || []).map(vendor => Number(vendor.value));
				const lineVendorId = Number(log?.orderresponse?.vendorId);

				if (selectedVendorIds.length > 0 && !selectedVendorIds.includes(lineVendorId)) {
					return false;
				}

				if (this.filters.status && log.status !== this.filters.status) {
					return false;
				}

				if (this.filters.search) {
					const statusLabel = this.getStatusLabel(log.status).toLowerCase();
					const search = this.filters.search.toLowerCase();


					return String(log.id).includes(search)
							|| String(log.ean ?? '').includes(search)
							|| statusLabel.includes(search)
							|| String(log?.additionalStatusInfo ?? '').toLowerCase().includes(search)
				}

				return true;
			});
		},
		selectedOrderResponses() {
			return this.orderResponseLineOrderLogs.filter(log => log.selected);
		},
		selectedUnmatchedOrderResponses() {
			return this.unmatchedOrderResponseLines.filter(log => log.selected);
		},
		vendorOptions() {
			return [
				...(this.vendors ?? []).map(vendor => ({
					value: vendor.id,
					text: vendor.name,
				})),
			];
		},
		rejectedOrderResponseLines() {
			return this.filteredOrderResponseLineOrderLogs.filter(log => Number(log.orderresponseline?.status) === 1);
		},
		partialShippedOrderResponseLines() {
			return this.filteredOrderResponseLineOrderLogs.filter(log => Number(log.orderresponseline?.status) === 2);
		},
		backorderOrderResponseLines() {
			return this.filteredOrderResponseLineOrderLogs.filter(log => Number(log.orderresponseline?.status) === 3);
		},
		unknownOrderResponseLines() {
			return this.filteredOrderResponseLineOrderLogs.filter(log => {
				const status = log.orderresponseline?.status;
				return status == null || ![0, 1, 2, 3].includes(Number(status));
			});
		},
		currentOrderResponseLines() {
			switch (this.activeStatusTab) {
				case 'rejected':
					return this.rejectedOrderResponseLines;
				case 'partialShipped':
					return this.partialShippedOrderResponseLines;
				case 'backorder':
					return this.backorderOrderResponseLines;
				case 'unknown':
					return this.unknownOrderResponseLines;
				default:
					return [];
			}
		},
		rejectedUnmatchedOrderResponseLines() {
			return this.filteredUnmatchedOrderResponseLines.filter(log => Number(log.status) === 1);
		},
		partialShippedUnmatchedOrderResponseLines() {
			return this.filteredUnmatchedOrderResponseLines.filter(log => Number(log?.status) === 2);
		},
		backorderUnmatchedOrderResponseLines() {
			return this.filteredUnmatchedOrderResponseLines.filter(log => Number(log?.status) === 3);
		},
		unknownUnmatchedOrderResponseLines() {
			return this.filteredUnmatchedOrderResponseLines.filter(log => {
				const status = log?.status;
				return status == null || ![0, 1, 2, 3].includes(Number(status));
			});
		},
		currentUnmatchedOrderResponseLines() {
			return this.filteredUnmatchedOrderResponseLines.filter(line => {
				const status = line.status;

				switch (this.activeStatusTab) {
					case 'rejected':
						return Number(status) === 1;
					case 'partialShipped':
						return Number(status) === 2;
					case 'backorder':
						return Number(status) === 3;
					case 'unknown':
						return status == null || ![0, 1, 2, 3].includes(Number(status));
					default:
						return false;
				}
			});
		},
		rejectOrderResponseCount() {
			return this.rejectedOrderResponseLines.length + this.rejectedUnmatchedOrderResponseLines.length;
		},
		partialShippedOrderResponseCount() {
			return this.partialShippedOrderResponseLines.length + this.partialShippedUnmatchedOrderResponseLines.length;
		},
		backorderOrderResponseCount() {
			return this.backorderOrderResponseLines.length + this.backorderUnmatchedOrderResponseLines.length;
		},
		unknownOrderResponseCount() {
			return this.unknownOrderResponseLines.length + this.unknownUnmatchedOrderResponseLines.length;
		},
	},
	methods: {
		getCustomerOrder(row) {
			return row?.item?.order_log?.customerorder ?? null;
		},
		toggleSelectAll(checked) {
			this.filteredOrderResponseLineOrderLogs.forEach(log => {
				log.selected = checked;
			});
		},
		toggleSelectAllUnmatched(checked) {
			this.unmatchedOrderResponseLines.forEach(log => {
				log.selected = checked;
			});
		},
		getStatusLabel(level) {
			switch (level) {
				case 0:
					return "confirmed";
				case 1:
					return "rejected / cancelled";
				case 2:
					return "partially shipped";
				case 3:
					return "backorder";
				default:
					return "unknown";
			}
		},
		getVendorName(vendorId) {
			const vendor = this.vendors.find((v) => v.id === vendorId);
			return vendor ? vendor.name : "Unknown Vendor";
		},
		getMarketAccount(row) {
			let name = row?.item?.order_log?.customerorder?.marketaccount.mdsMarketIdentifier
			if(name !== null){
				return name;
			} else {
				return row?.item?.order_log?.customerorder?.marketaccount.name;
			}
		},
		async runMassAction(isUnmatchedOrderResponseLine = false) {
			if ((!this.selectedMassAction || this.selectedOrderResponses.length === 0 )
			&& (!this.selectedUnmatchedMassAction || this.selectedUnmatchedOrderResponses.length === 0)) {
				return;
			}
			this.isMassAction = true;

			if (this.selectedMassAction === 'delete' || this.selectedUnmatchedMassAction === 'delete') {
				try {

					const orderResponses = isUnmatchedOrderResponseLine ? [...this.selectedUnmatchedOrderResponses] : [...this.selectedOrderResponses];

					for (const orderResponse of orderResponses) {
						await this.markAsProcessed(orderResponse, this.isMassAction, isUnmatchedOrderResponseLine);
					}

					this.$bvToast.toast(`Erfolgreich ${orderResponses.length} Orderresponses gelöscht`, {
						title: 'Orderresponses gelöscht',
						variant: 'success',
						solid: true,
					});

				} catch (e) {
					console.error("Error during mass delete action:", e);
					this.modalError = {
						show: true,
						error: e,
						title: 'Fehler beim Löschen der Orderresponses',
					};

				} finally {
					this.isMassAction = false;
					this.selectedMassAction = null;
				}
			}
		},
		unfreezeOrderWNC(orderResponseLine){
			let order = orderResponseLine?.order_log?.customerorder;
			this.httpRequestInProgress = true;
				axios.post('customerorder/unfreezewnc/' + order.id)
					.then(() => {
						console.log("Order unfreeze successful for order id:", order.id);
						if(!this.shouldCreateVendorCancellation){
							this.httpRequestInProgress = false;
							this.markAsProcessed(orderResponseLine);
							return;
						}
						useCustomerOrderActions().createVendorCancellation(order)
						.then(res => {
							this.httpRequestInProgress = false;
							console.log(res)
							this.markAsProcessed(orderResponseLine);
						}).catch(
						error => {
							this.httpRequestInProgress = false;
							this.modalError = {
								show: true,
								error: error,
								title: 'Fehler beim Erstellen der Stornierung',
							};
							console.error(error);
						});
					})
					.catch((error) => {
						this.httpRequestInProgress = false;
						this.modalError = {
							show: true,
							error: error,
							title: 'Fehler beim Freigeben der Bestellung',
						};
						console.error(error);
					});
		},
		//after successful action or pressing delete button, mark the order response as processed and remove it from the table
		//in the backend a timestamp for "processedAt" is set
		//orderResponse is a synonym in this function for both orderResponseLine and orderResponseLineOrderLog
		markAsProcessed(orderResponse, isMassAction = false, isUnmatchedOrderResponseLine) {
			this.httpRequestInProgress = true;
			axios.post("/orderresponse/markprocessed/" + orderResponse.id, { isOrderResponseLine: isUnmatchedOrderResponseLine })
				.then(() => {
					this.httpRequestInProgress = false;
					//remove the processed order response from the table

					 if(isUnmatchedOrderResponseLine){
						 const index = this.unmatchedOrderResponseLines.findIndex((or) => or.id === orderResponse.id);
						 if (index !== -1) {
							 this.unmatchedOrderResponseLines.splice(index, 1);
						 }
					 } else {
						 const index = this.orderResponseLineOrderLogs.findIndex((or) => or.id === orderResponse.id);
						 if (index !== -1) {
							 this.orderResponseLineOrderLogs.splice(index, 1);
						 }
					 }
					if(isMassAction === false){
						this.$bvToast.toast("Order Response erfolgreich bearbeitet", {
							title: "Order Response",
							variant: "success",
							solid: true,
							autoHideDelay: 2000,
						});
					}
				})
				.catch((error) => {
					this.httpRequestInProgress = false;
					this.modalError = {
						show: true,
						error: error,
						title: 'Fehler beim Markieren als verarbeitet',
					};
					console.error(error);
				});
		},
		openDeleteModal(orderResponse) {
			this.orderResponseToDelete = orderResponse;
			this.deleteModalState = true;
		},
		openUnfreezeModal(orderResponse) {
			this.orderResponseToUnfreeze = orderResponse;
			this.unfreezeModalState = true;
		},

		getArticlePriceNet(row) {
			return this.getCustomerOrder(row)?.customerorderitems?.[0]?.articlePriceClear ?? null;
		},
		getOriginalCurrencyText(row) {
			const customerOrder = this.getCustomerOrder(row);
			const value = this.getArticlePriceNet(row);

			return this.formatOriginalCurrency(value, customerOrder);
		},
		formatEuroCurrency(customerOrder, value) {
			if (!customerOrder) return "-";
			return `${Number.parseFloat(
					customerOrder?.currencyToEuro * value
			).toFixed(2)}€`;
		},
		formatOriginalCurrency(value, customerOrder) {
			return currencyFormatting.formatOriginalCurrency(value, customerOrder);
		},
		disableButton(order){
			return this.httpRequestInProgress
				|| [ this.orderStates.codes.DONE,
					this.orderStates.codes.REFUND,
					this.orderStates.codes.REFUND_OK,
					this.orderStates.codes.WAIT,
					this.orderStates.codes.OPEN
				].includes(order.orderState);
		},
		resetFilters() {
			this.filters.vendorIds = [];
			this.filters.search = '';
		},
	},
	created(){
		axios.get("/user/authorization").then(res => {
			this.authorization = res.data;
		}).catch(error => {
			this.modalError = {
				show: true,
				error: error,
				title: 'Fehler beim Laden der Berechtigungen',
			};
			console.error(error);
		});
	}
}
</script>

<style scoped>
:deep .order-response-vendor-select.multiselect,
:deep .order-response-vendor-select .multiselect__tags {
	min-height: 29px !important;
	height: auto !important;
}

</style>